import { useEffect, useState } from "react";

const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 2;

/**
 * `/images/news/12/47/487/orig.jpeg` + 300x200 + cover
 *   → `/images/news/12/47/487/300_200_cover.webp`
 */
export function buildResizedSrc(src, width, height, fit = "inside") {
  if (!src) return "";
  const slash = src.lastIndexOf("/");
  if (slash === -1) return src;
  const fitSuffix = fit === "cover" ? "_cover" : "";
  return `${src.slice(0, slash)}/${width}_${height}${fitSuffix}.webp`;
}

function probeImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

/**
 * Картинка с уменьшением «на лету».
 * POST /api/images/resize с id изображения; опрос каждые 3 с, максимум 2 попытки.
 */
export default function ResizedImage({
  imageId,
  src = "",
  width,
  height,
  fit = "inside",
  reset = false,
  alt = "",
  title,
  className,
  style,
  ...rest
}) {
  const resizedSrc = src ? buildResizedSrc(src, width, height, fit) : "";
  const [currentSrc, setCurrentSrc] = useState(resizedSrc);

  useEffect(() => {
    if (!imageId || !resizedSrc) {
      setCurrentSrc("");
      return undefined;
    }

    let cancelled = false;
    setCurrentSrc(resizedSrc);

    const run = async () => {
      if (!reset && (await probeImage(resizedSrc))) {
        return;
      }

      try {
        await fetch("/api/images/resize", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: imageId,
            width,
            height,
            fit,
            reset,
          }),
        });
      } catch (e) {
        // продолжаем опрос
      }

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
        if (cancelled) return;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, POLL_INTERVAL_MS);
        });
        const probeUrl = `${resizedSrc}?r=${attempt}`;
        // eslint-disable-next-line no-await-in-loop
        if (await probeImage(probeUrl)) {
          if (!cancelled) setCurrentSrc(resizedSrc);
          return;
        }
      }

      if (!cancelled && src) {
        setCurrentSrc(src);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [imageId, resizedSrc, src, width, height, fit, reset]);

  if (!currentSrc) return null;

  const imageStyle = {
    width,
    height,
    objectFit: fit === "cover" ? "cover" : "contain",
    display: "block",
    ...style,
  };

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={currentSrc}
      width={width}
      height={height}
      alt={alt}
      title={title ?? alt}
      className={className}
      style={imageStyle}
      onError={() => {
        if (src && currentSrc !== src) {
          setCurrentSrc(src);
        }
      }}
      {...rest}
    />
  );
}
